import { getLogger } from '@trimble/logging';
import { UnreachableCaseError } from 'ts-essentials';

/**
 * Structured tile fetch debug log buffer for Phase 0 baseline measurement.
 * Enable from the browser console: `window.__featuresTileDebugLogs.enable()`.
 *
 * Filter captured entries with `getLogs({ search: 'per-layer' })` or
 * cross-check Network tab URLs against `getLogs({ mode: 'request' })`.
 */

export type FeaturesTileDebugLogLevel = 'debug' | 'warn' | 'info' | 'error';

export type FeaturesTileDebugLogMode =
  | 'request'
  | 'combined'
  | 'per-layer'
  | 'concurrency'
  | 'other';

export interface FeaturesTileDebugLogEntry {
  readonly timestamp: number;
  readonly level: FeaturesTileDebugLogLevel;
  readonly message: string;
  readonly mode: FeaturesTileDebugLogMode;
  readonly context?: Record<string, unknown>;
}

export interface FeaturesTileDebugLogFilter {
  readonly level?: FeaturesTileDebugLogLevel;
  readonly mode?: FeaturesTileDebugLogMode;
  readonly search?: string;
}

export interface FeaturesTileDebugLogSnapshot {
  readonly totalCount: number;
  readonly requestCount: number;
  readonly perLayerRequestCount: number;
  readonly combinedRequestCount: number;
  readonly warnCount: number;
  readonly startedAt: number | null;
  readonly elapsedMs: number | null;
}

const DEFAULT_MAX_ENTRIES = 500;

const inferLogMode = (message: string): FeaturesTileDebugLogMode => {
  if (message.includes('request (per-layer)')) {
    return 'per-layer';
  }

  if (message.includes('request (combined)')) {
    return 'combined';
  }

  if (message.includes('layer/job requests with concurrency')) {
    return 'concurrency';
  }

  if (message.includes('[FeaturesService] request')) {
    return 'request';
  }

  return 'other';
};

class FeaturesTileDebugLogsTracker {
  private enabled = false;
  private readonly entries: FeaturesTileDebugLogEntry[] = [];
  private startedAt: number | null = null;
  private readonly maxEntries: number;

  constructor(maxEntries = DEFAULT_MAX_ENTRIES) {
    this.maxEntries = maxEntries;
  }

  public enable(): void {
    this.enabled = true;
    this.reset();
  }

  public disable(): void {
    this.enabled = false;
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public reset(): void {
    this.entries.length = 0;
    this.startedAt = performance.now();
  }

  public record(
    level: FeaturesTileDebugLogLevel,
    message: string,
    context?: Record<string, unknown>
  ): void {
    if (!this.enabled) {
      return;
    }

    if (this.startedAt === null) {
      this.startedAt = performance.now();
    }

    this.entries.push({
      timestamp: performance.now(),
      level,
      message,
      mode: inferLogMode(message),
      context,
    });

    if (this.entries.length > this.maxEntries) {
      this.entries.shift();
    }
  }

  public getLogs(
    filter?: FeaturesTileDebugLogFilter
  ): FeaturesTileDebugLogEntry[] {
    let results = [...this.entries];

    if (filter?.level) {
      results = results.filter((entry) => entry.level === filter.level);
    }

    if (filter?.mode) {
      results = results.filter((entry) => entry.mode === filter.mode);
    }

    if (filter?.search) {
      const search = filter.search.toLowerCase();
      results = results.filter(
        (entry) =>
          entry.message.toLowerCase().includes(search) ||
          JSON.stringify(entry.context ?? {})
            .toLowerCase()
            .includes(search)
      );
    }

    return results;
  }

  public getSnapshot(): FeaturesTileDebugLogSnapshot {
    const elapsedMs =
      this.startedAt !== null ? performance.now() - this.startedAt : null;

    return {
      totalCount: this.entries.length,
      requestCount: this.entries.filter((entry) => entry.mode === 'request')
        .length,
      perLayerRequestCount: this.entries.filter(
        (entry) => entry.mode === 'per-layer'
      ).length,
      combinedRequestCount: this.entries.filter(
        (entry) => entry.mode === 'combined'
      ).length,
      warnCount: this.entries.filter((entry) => entry.level === 'warn').length,
      startedAt: this.startedAt,
      elapsedMs,
    };
  }

  public formatSummary(): string {
    const snapshot = this.getSnapshot();

    return (
      `[FeaturesTileDebugLogs] total=${snapshot.totalCount}, ` +
      `per-layer=${snapshot.perLayerRequestCount}, ` +
      `combined=${snapshot.combinedRequestCount}, ` +
      `warn=${snapshot.warnCount}, ` +
      `elapsedMs=${snapshot.elapsedMs?.toFixed(0) ?? 'n/a'}`
    );
  }
}

export const featuresTileDebugLogs = new FeaturesTileDebugLogsTracker();

/**
 * Forwards to `@trimble/logging` only when tile debug logs are enabled via
 * `window.__featuresTileDebugLogs.enable()`. Warnings and errors always log.
 */
export function logFeaturesTileDebug(
  logger: ReturnType<typeof getLogger>,
  level: FeaturesTileDebugLogLevel,
  message: string,
  context?: Record<string, unknown>
): void {
  const isEnabled = featuresTileDebugLogs.isEnabled();

  if (isEnabled) {
    featuresTileDebugLogs.record(level, message, context);
  }

  if (!isEnabled && level !== 'warn' && level !== 'error') {
    return;
  }

  switch (level) {
    case 'debug':
      logger.debug(message, context);
      break;
    case 'warn':
      logger.warn(message, context);
      break;
    case 'info':
      logger.info(message, context);
      break;
    case 'error':
      logger.error(message, context);
      break;
    default:
      throw new UnreachableCaseError(level);
  }
}

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- DevTools debug global
    __featuresTileDebugLogs?: FeaturesTileDebugLogsTracker;
  }
}

if (typeof window !== 'undefined') {
  window.__featuresTileDebugLogs = featuresTileDebugLogs;
}
